import React, { forwardRef, useImperativeHandle, useState } from 'react';
import styled from '@emotion/styled';
import { IEnrichedProduct, IEnrichedThemaCode, IReview } from '$models';
import { useTranslation } from '$hooks';
import { Flex } from '$components/layouts';
import { SidePanel } from '$components/elements/side-panel';
import { About, Details, Reviews, Themas } from './side-panel-content';
import { ModalTrackingTypes } from '$constants';
import { mq } from '$lib/helpers';

export type ProductInfoTypes = 'about' | 'description' | 'details' | 'reviews' | 'themas';

type SidePanelOption = {
    open: boolean;
    type?: ProductInfoTypes;
};

type Props = {
    product: IEnrichedProduct;
    reviews: IReview;
    themas?: IEnrichedThemaCode[][];
    onOpenReviewModal: () => void;
};

export type ProductInfoSidePanelRef = {
    open(type: ProductInfoTypes): void;
    close(): void;
};

export const ProductInfoSidePanel = forwardRef<ProductInfoSidePanelRef, Props>((props: Props, ref) => {
    const { translate } = useTranslation();
    const [contentSitePanel, setContentSitePanel] = useState<SidePanelOption>({ open: false });

    const onOpenReviewModal = () => {
        setContentSitePanel({ open: false });
        props?.onOpenReviewModal?.();
    };

    useImperativeHandle(ref, () => ({
        open(type) {
            setContentSitePanel({ open: true, type });
        },
        close() {
            setContentSitePanel({ open: false });
        },
    }));

    const getTitle = (type?: ProductInfoTypes) => {
        switch (type) {
            case 'about':
                return translate('productPage.linkHeaders.about');
            case 'details':
                return translate('productPage.linkHeaders.details');
            case 'description':
                return translate('productPage.linkHeaders.description');
            case 'reviews':
                return translate('productPage.linkHeaders.reviews');
            case 'themas':
                return translate('productPage.linkHeaders.themas');
            default:
                return '';
        }
    };
    const getTrackingType = (type?: ProductInfoTypes) => {
        switch (type) {
            case 'about':
                return ModalTrackingTypes.ProductPageAbout;
            case 'details':
                return ModalTrackingTypes.ProductPageDetails;
            case 'description':
                return ModalTrackingTypes.ProductPageDescription;
            case 'reviews':
                return ModalTrackingTypes.ProductPageReviews;
            case 'themas':
                return ModalTrackingTypes.ProductPageThemas;
            default:
                return '';
        }
    };

    return (
        <SidePanel
            show={contentSitePanel.open}
            onChange={(change) => setContentSitePanel({ ...contentSitePanel, open: change })}
            title={getTitle(contentSitePanel.type)}
            modalTrackType={getTrackingType(contentSitePanel.type)}
            mobileStyle
            maxWidth={768}
        >
            <ContentContainer>
                {contentSitePanel.type === 'about' ||
                    (contentSitePanel.type === 'description' && <About product={props.product} />)}
                {/* {contentSitePanel.type === 'description' && <Description product={props.product} />} */}
                {contentSitePanel.type === 'details' && <Details product={props.product} />}
                {contentSitePanel.type === 'reviews' && (
                    <Reviews
                        product={props.product?.product}
                        reviews={props.reviews}
                        onOpenReviewModal={onOpenReviewModal}
                    />
                )}
                {contentSitePanel.type === 'themas' && <Themas themaCodes={props.themas} />}
            </ContentContainer>
        </SidePanel>
    );
});

const ContentContainer = styled(Flex)(({ theme }) => ({
    padding: `0 ${theme.sizes.siteGutter}`,
    marginBottom: theme.space[8],

    [mq('sm')]: {
        padding: `0 ${theme.space[6]}`,
    },
}));
